import { fileRecordListScope } from "@/api/fileRecordKeys";
import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import {
  blockStateFileRecordId,
  useBlockStateSource,
} from "@/pages/canvas/hooks/useBlockStateSource";
import { useHtmlBlockEditSession } from "@/pages/canvas/hooks/useHtmlBlockEditSession";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  HtmlBlockCardSwitch,
  useThemeContext,
  type BlockDisplay,
  type BlockToParentMessage,
} from "@cohesive/ui-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoadingOverlay } from "../LoadingOverlay";

/**
 * An HTML BLOCK in the detail pane, rendered fitted and editable.
 *
 * A block is authored to fill an exact box — fixed layouts, `overflow:
 * hidden`, flex tracks that assume the authored footprint. The document
 * viewer (`HtmlContent`) gives it the pane's own width and full height,
 * which clips and stretches it into something unrecognizable. So a block
 * gets the canvas treatment instead: the same `HtmlBlockCardSwitch` the
 * node and the lightbox mount, on a stage scaled to the pane — zoom of the
 * authored layout, never reflow.
 *
 * Editing follows the lightbox's model exactly: islands are contenteditable
 * whenever the user can edit, backed by this surface's own
 * `useHtmlBlockEditSession` (island content arrives over the runtime bridge,
 * saves go through the shared region PATCH), and content stays frozen while
 * the session is live so refetches never yank the document mid-edit.
 */

const BLOCK_STAGE_WIDTH = 800;
const BLOCK_STAGE_HEIGHT = 608;
/** Breathing room around the fitted stage. Near-zero: the pane is already
 *  narrow, and every pixel here comes off the block's own width. */
const STAGE_MARGIN = 4;
/** Zoom ceiling for the width-fit — the lightbox's own cap. A small block
 *  magnifies to use the pane, but never into a blur. */
const BLOCK_MAX_SCALE = 2;
/** How long content-size reports may accumulate before the grown height is
 *  applied when the runtime never signals `settled` (older runtimes). */
const SETTLE_CAP_MS = 2500;

export function BlockContent({ record }: { record: FileRecord }) {
  const { mode: theme } = useThemeContext();
  // The record remembers its box — authored at create, refreshed when the
  // block was promoted. The fixed stage is only the legacy fallback.
  const stageWidth = record.display?.width || BLOCK_STAGE_WIDTH;
  const stageHeight = record.display?.height || BLOCK_STAGE_HEIGHT;
  const canEdit = record.can_edit !== false;
  const blockState = useBlockStateSource(blockStateFileRecordId(record));
  const display: BlockDisplay = useMemo(
    () => ({
      mode: "block",
      chromeless: record.display?.chromeless === true,
      layer: "front",
    }),
    [record.display?.chromeless],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () =>
      setFit({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Embedded `/public/{id}` media is unreachable while the referenced record
  // is unpublished; the card presigns through this, and island saves reverse
  // the rewrite so presigned URLs are never persisted.
  const presign = useHtmlPresignRewrite({
    fileRecordId: record.id,
    scope: fileRecordListScope(record),
  });

  const session = useHtmlBlockEditSession({
    fileRecordId: record.id,
    editing: canEdit,
    reversePresignedUrls: presign.reversePresignedUrls,
  });
  // The canvas flushes on its focus latch's falling edge; this surface's edge
  // is unmount — the mount is keyed by record, so navigating away from the
  // block (or closing the pane) saves whatever is pending.
  const flushRef = useRef(session.flush);
  flushRef.current = session.flush;
  useEffect(() => () => flushRef.current(), []);

  // Some blocks reveal more content the taller their box is (clip-don't-
  // scroll layouts) rather than scaling. The runtime reports the content's
  // measured extent; the frame grows to it ONCE, from measurements taken
  // while the frame still sits at the authored height — the lightbox's rule,
  // so a 100%-height layout can never enter a grow/re-measure feedback loop.
  // Early reports land before fonts and layout settle and under-measure, so
  // reports ACCUMULATE (max) until the runtime's `settled` signal — or a cap,
  // for older runtimes that never send it — and apply then, frozen after.
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const maxReportRef = useRef(0);
  const appliedRef = useRef(false);
  const capTimerRef = useRef<number | null>(null);
  // Freezing requires a measurement: a cap that fired before any report (slow
  // fetch, cold presign) must leave the capture open, or a tall block would be
  // pinned to its authored height forever. The cap therefore arms on the
  // FIRST report — covering runtimes that never send `settled` — and `settled`
  // applies immediately.
  const applyContentHeight = useCallback(() => {
    if (appliedRef.current || maxReportRef.current <= 0) return;
    appliedRef.current = true;
    setContentHeight(maxReportRef.current);
  }, []);
  useEffect(
    () => () => {
      if (capTimerRef.current !== null)
        window.clearTimeout(capTimerRef.current);
    },
    [],
  );
  const handleBlockMessage = useCallback(
    (msg: BlockToParentMessage) => {
      if (msg.type === "content-size") {
        if (!appliedRef.current && msg.height > maxReportRef.current) {
          maxReportRef.current = msg.height;
          if (capTimerRef.current === null) {
            capTimerRef.current = window.setTimeout(
              applyContentHeight,
              SETTLE_CAP_MS,
            );
          }
        }
        return;
      }
      if (msg.type === "settled") {
        applyContentHeight();
        return;
      }
      session.onIslandMessage(msg);
    },
    [applyContentHeight, session],
  );

  // Fit model: the document treatment, at the block's fixed aspect. The
  // stage fits the pane's width — magnifying small blocks up to the zoom
  // ceiling — and when the scaled height outgrows the pane, the pane
  // scrolls the stage instead of shrinking it into a thumbnail.
  const measured = fit.width > 0 && fit.height > 0;
  const frameHeight = Math.max(stageHeight, contentHeight ?? 0);
  const scale = Math.min(
    BLOCK_MAX_SCALE,
    Math.max(0.1, (fit.width - STAGE_MARGIN * 2) / stageWidth),
  );
  const scrollFit = frameHeight * scale > fit.height - STAGE_MARGIN * 2;

  return (
    <div
      ref={containerRef}
      className={cn(
        "bg-n-05 flex h-full w-full justify-center",
        scrollFit
          ? "items-start overflow-y-auto"
          : "items-center overflow-hidden",
      )}
    >
      {!measured ? (
        <LoadingOverlay />
      ) : (
        <div
          className="border-n-10 relative shrink-0 overflow-hidden rounded-lg border shadow-sm"
          style={{
            width: stageWidth * scale,
            height: frameHeight * scale,
            marginTop: scrollFit ? STAGE_MARGIN : undefined,
            marginBottom: scrollFit ? STAGE_MARGIN : undefined,
          }}
        >
          {/* Authored-size box, zoomed visually — the same structure the
              lightbox uses, so pointer coordinates map through the card's
              own rect/offsetWidth conversion. */}
          <div
            style={{
              width: stageWidth,
              height: frameHeight,
              transform: scale !== 1 ? `scale(${scale})` : undefined,
              transformOrigin: "top left",
            }}
          >
            <HtmlBlockCardSwitch
              kind={record.display?.kind}
              url={record.url}
              path={record.path}
              updated_at={record.updated_at}
              display={display}
              theme={theme}
              transformHtml={presign.transformHtml}
              isInteractive
              islandsEditable={canEdit}
              contentFrozen={session.contentFrozen}
              onBlockMessage={handleBlockMessage}
              blockState={blockState}
            />
          </div>
        </div>
      )}
    </div>
  );
}
